import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Search, Truck } from "lucide-react";
import { type FormEvent, useState } from "react";
import { api, jsonBody, queryString } from "../../api/client";
import type { ListResponse, Vendor } from "../../api/types";
import { useAuth } from "../../auth/AuthProvider";
import { membershipAllowsIn } from "../../auth/permissions";
import { titleCase } from "../../lib/format";
import {
  Card,
  ConfirmDialog,
  DataTable,
  EmptyState,
  ErrorPanel,
  Field,
  FormActions,
  Metric,
  Modal,
  Notice,
  PageHeader,
  StatusPill,
} from "../../ui/components";
import { ListPageSkeleton } from "../../ui/skeletons";

interface VendorDraft {
  id?: string;
  name: string;
  category: string;
  contactName: string;
  email: string;
  phone: string;
  notes: string;
  active: boolean;
  version: number;
}

const emptyDraft: VendorDraft = {
  name: "",
  category: "",
  contactName: "",
  email: "",
  phone: "",
  notes: "",
  active: true,
  version: 0,
};

function draftFrom(vendor: Vendor): VendorDraft {
  return {
    id: vendor.id,
    name: vendor.name,
    category: vendor.category,
    contactName: vendor.contactName,
    email: vendor.email,
    phone: vendor.phone,
    notes: vendor.notes,
    active: vendor.active,
    version: vendor.version,
  };
}

// What the server will accept, written once. The identifier travels in the address and the draft
// carries one for its own use, and the server refuses a body containing a field it does not know:
// spreading the draft into the request is how a supplier stops being retirable without anybody
// touching the retire button.
function vendorPayload(draft: VendorDraft, active: boolean = draft.active) {
  return {
    name: draft.name,
    category: draft.category,
    contactName: draft.contactName,
    email: draft.email,
    phone: draft.phone,
    notes: draft.notes,
    active,
    version: draft.version,
  };
}

export function VendorsPage() {
  const auth = useAuth();
  const queryClient = useQueryClient();
  // The directory is the client's own list of suppliers, kept on the events side because hiring one
  // in is the client spending their own money. The server gates these routes the same way, so a
  // lettings-only role is never offered a control it would be refused.
  const membership = auth.session?.selectedMembership;
  const canManage = membershipAllowsIn(membership, "events", "vendors.manage");
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [includeRetired, setIncludeRetired] = useState(false);
  const [draft, setDraft] = useState<VendorDraft | null>(null);
  const [retiring, setRetiring] = useState<Vendor | null>(null);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<unknown>();

  const filters = queryString({ search, category, includeRetired: includeRetired || undefined });
  const vendors = useQuery({
    queryKey: ["vendors", filters],
    queryFn: () => api<ListResponse<Vendor>>(`/api/v1/vendors${filters}`),
  });
  // The directory is asked for again without the filters so the category list stays complete while
  // somebody is filtering by one. A list that shrank to the category already chosen would be a
  // dead end nobody could get out of.
  const everyVendor = useQuery({
    queryKey: ["vendors", "categories"],
    queryFn: () => api<ListResponse<Vendor>>("/api/v1/vendors?includeRetired=true"),
  });

  async function save(formEvent: FormEvent<HTMLFormElement>) {
    formEvent.preventDefault();
    if (!draft) return;
    setWorking(true);
    setError(undefined);
    try {
      await api(draft.id ? `/api/v1/vendors/${draft.id}` : "/api/v1/vendors", {
        method: draft.id ? "PUT" : "POST",
        body: jsonBody(vendorPayload(draft)),
      });
      await queryClient.invalidateQueries({ queryKey: ["vendors"] });
      setDraft(null);
    } catch (caught) {
      setError(caught);
    } finally {
      setWorking(false);
    }
  }

  // Retiring is an ordinary edit with the flag turned over, because that is exactly what it is.
  // The record stays, the engagements that name it stay, and the directory stops offering it.
  async function setRetired(vendor: Vendor, active: boolean) {
    setWorking(true);
    setError(undefined);
    try {
      await api(`/api/v1/vendors/${vendor.id}`, {
        method: "PUT",
        body: jsonBody(vendorPayload(draftFrom(vendor), active)),
      });
      await queryClient.invalidateQueries({ queryKey: ["vendors"] });
      setRetiring(null);
    } catch (caught) {
      setError(caught);
    } finally {
      setWorking(false);
    }
  }

  if (vendors.isLoading)
    return (
      <ListPageSkeleton
        title="Vendors"
        description="The suppliers this organization brings in: catering, sound, security, rentals."
        filterCount={3}
        columnCount={7}
      />
    );
  if (vendors.error) return <ErrorPanel error={vendors.error} />;
  const items = vendors.data?.items ?? [];
  const categories = [
    ...new Set((everyVendor.data?.items ?? []).map((vendor) => vendor.category).filter(Boolean)),
  ].sort();
  const engagements = items.reduce((total, vendor) => total + vendor.serviceCount, 0);

  return (
    <>
      <PageHeader
        title="Vendors"
        description="The suppliers this organization brings in: catering, sound, security, rentals. Gear you own and count back into a store lives in inventory instead."
        actions={
          canManage ? (
            <button
              className="button primary"
              type="button"
              onClick={() => {
                setError(undefined);
                setDraft(emptyDraft);
              }}
            >
              <Plus size={17} aria-hidden="true" /> Add supplier
            </button>
          ) : null
        }
      />
      {error ? <ErrorPanel error={error} /> : null}
      <div className="metrics-grid">
        <Metric label="Suppliers listed" value={items.length} />
        <Metric
          label="Currently offered"
          value={items.filter((vendor) => vendor.active).length}
          hint="Retired suppliers keep their history"
        />
        <Metric label="Engagements" value={engagements} hint="Across events and rentals" />
      </div>
      <Card>
        <div className="filters">
          <Field label="Search suppliers">
            <span className="inline">
              <Search size={18} aria-hidden="true" />
              <input
                type="search"
                aria-label="Search suppliers"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Name, contact, or email"
              />
            </span>
          </Field>
          <Field label="Category">
            <select value={category} onChange={(event) => setCategory(event.target.value)}>
              <option value="">Every category</option>
              {categories.map((value) => (
                <option key={value} value={value}>
                  {titleCase(value)}
                </option>
              ))}
            </select>
          </Field>
          <label className="check-field">
            <input
              type="checkbox"
              checked={includeRetired}
              onChange={(event) => setIncludeRetired(event.target.checked)}
            />
            <span>Include retired suppliers</span>
          </label>
        </div>
      </Card>
      <Card>
        {items.length ? (
          <DataTable
            caption="Suppliers this organization brings in"
            items={items}
            getKey={(vendor: Vendor) => vendor.id}
            defaultSort={{ label: "Supplier", direction: "ascending" }}
            columns={[
              {
                label: "Supplier",
                sortValue: (vendor) => vendor.name.toLowerCase(),
                render: (vendor) => (
                  <span className="inline">
                    <Truck size={16} aria-hidden="true" />
                    <span>
                      <strong>{vendor.name}</strong>
                      <br />
                      <small>{vendor.notes || "No notes recorded"}</small>
                    </span>
                  </span>
                ),
              },
              {
                label: "Category",
                sortValue: (vendor) => vendor.category,
                render: (vendor) => titleCase(vendor.category),
              },
              {
                label: "Who to ask for",
                render: (vendor) => vendor.contactName || "Not recorded",
              },
              { label: "Email", render: (vendor) => vendor.email || "Not recorded" },
              { label: "Phone", render: (vendor) => vendor.phone || "Not recorded" },
              {
                label: "Engagements",
                className: "numeric",
                sortValue: (vendor) => vendor.serviceCount,
                render: (vendor) => vendor.serviceCount,
              },
              {
                label: "Status",
                sortValue: (vendor) => (vendor.active ? "active" : "retired"),
                render: (vendor) => <StatusPill value={vendor.active ? "active" : "retired"} />,
              },
              ...(canManage
                ? [
                    {
                      label: "Actions",
                      render: (vendor: Vendor) => (
                        <div className="inline">
                          <button
                            className="button ghost small"
                            type="button"
                            onClick={() => {
                              setError(undefined);
                              setDraft(draftFrom(vendor));
                            }}
                          >
                            Edit
                          </button>
                          {vendor.active ? (
                            <button
                              className="button ghost small"
                              type="button"
                              disabled={working}
                              onClick={() => {
                                setError(undefined);
                                setRetiring(vendor);
                              }}
                            >
                              Retire
                            </button>
                          ) : (
                            <button
                              className="button ghost small"
                              type="button"
                              disabled={working}
                              onClick={() => void setRetired(vendor, true)}
                            >
                              Bring back
                            </button>
                          )}
                        </div>
                      ),
                    },
                  ]
                : []),
            ]}
          />
        ) : (
          <EmptyState
            title="No suppliers listed"
            description="Add the caterers, sound companies and other suppliers you bring in, then attach them to an event or a rental as engagements."
          />
        )}
      </Card>
      {!canManage ? (
        <Notice tone="info">
          Your role can read the supplier directory but not change it. Ask somebody who runs the
          program or the venue.
        </Notice>
      ) : null}

      <Modal
        open={draft !== null}
        onClose={() => setDraft(null)}
        title={draft?.id ? "Change this supplier" : "Add a supplier"}
        description="One record per relationship, so the same caterer is not entered three times by three people who cannot see each other's copy."
      >
        {draft ? (
          <form className="form-stack" onSubmit={save}>
            <Field label="Name">
              <input
                data-autofocus
                required
                maxLength={160}
                value={draft.name}
                onChange={(event) => setDraft({ ...draft, name: event.target.value })}
              />
            </Field>
            <div className="form-grid">
              <Field
                label="Category"
                hint="Catering, audio visual, security, whatever you group them by."
              >
                <input
                  maxLength={60}
                  list="vendor-categories"
                  value={draft.category}
                  onChange={(event) => setDraft({ ...draft, category: event.target.value })}
                />
              </Field>
              {/* Outside the field rather than in it: the field labels a single control, and a
                  second child would leave the label pointing at nothing a person can type in. */}
              <datalist id="vendor-categories">
                {categories.map((value) => (
                  <option key={value} value={value} />
                ))}
              </datalist>
              <Field label="Who to ask for">
                <input
                  maxLength={160}
                  value={draft.contactName}
                  onChange={(event) => setDraft({ ...draft, contactName: event.target.value })}
                />
              </Field>
              <Field label="Email">
                <input
                  type="email"
                  maxLength={254}
                  value={draft.email}
                  onChange={(event) => setDraft({ ...draft, email: event.target.value })}
                />
              </Field>
              <Field label="Phone">
                <input
                  maxLength={40}
                  value={draft.phone}
                  onChange={(event) => setDraft({ ...draft, phone: event.target.value })}
                />
              </Field>
            </div>
            <Field label="Notes" hint="Access, lead times, anything the next person needs to know.">
              <textarea
                maxLength={10000}
                value={draft.notes}
                onChange={(event) => setDraft({ ...draft, notes: event.target.value })}
              />
            </Field>
            <label className="check-field">
              <input
                type="checkbox"
                checked={draft.active}
                onChange={(event) => setDraft({ ...draft, active: event.target.checked })}
              />
              <span>Offer this supplier when arranging an engagement</span>
            </label>
            <FormActions
              submitting={working}
              submitLabel={draft.id ? "Save the supplier" : "Add the supplier"}
              onCancel={() => setDraft(null)}
            />
          </form>
        ) : null}
      </Modal>

      <ConfirmDialog
        open={retiring !== null}
        title="Retire this supplier"
        description={
          retiring
            ? `${retiring.name} stops being offered when somebody arranges an engagement. The ${retiring.serviceCount} engagement${retiring.serviceCount === 1 ? "" : "s"} already naming them stay exactly as they are.`
            : ""
        }
        confirmLabel="Retire the supplier"
        submitting={working}
        onClose={() => setRetiring(null)}
        onConfirm={() => {
          if (retiring) void setRetired(retiring, false);
        }}
      />
    </>
  );
}
